import { useState } from "react";
import { v4 as uuid } from "uuid";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { useSelector } from "react-redux";
import config from "config/config";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import { saveAgentAssistant } from "service/userservice/saveAgentAssistant";
import { updateAgentAssistant } from "service/userservice/updateAgentAssistant";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import isAllowedEmail from "./utils/isAllowedEmail";
import { useTranslation } from "react-i18next";

export default function AssistantInfoDialog({
  dialogState,
  setDialogState,
  title,
  description,
  assistantfname,
  assistantlname,
  assistantmobile,
  assistantemail,
  assistantzip,
  assistantviewcommission,
  operation,
  assistantuserid,
  getuserPermissions,
  setPageLoad,
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const UUID = uuid();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors: registrationErrors },
  } = useForm({
    defaultValues: {
      phoneNumber: assistantmobile,
    },
  });
  const { user } = useSelector((s) => s.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [errorInfo, seterrorInfo] = useState("");
  const [isChecked, setisChecked] = useState({
    viewCommissions: assistantviewcommission === 1 ? "Yes" : "No",
  });
  const handleOnChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.value;
    setisChecked({ ...isChecked, [name]: value });
  };
  const isDisabled = operation === "view";
  async function onSubmit(data) {
    if (csrUser?.userId) {
      alert(t("AgentAssistantInfoDialog-AssistantSettings"));
      return;
    }

    setLoading(true);
    if (operation === "add") {
      await saveAgentAssistant(
        user?.agentID,
        user?.alternateAgentID,
        user?.identityNumber,
        data.firstname,
        data.lastname,
        data.phoneNumber,
        data.email,
        data.postalCode,
        isChecked.viewCommissions === "Yes" ? 1 : 0
      )
        .then((data) => {
          setPageLoad(0);
          getuserPermissions();
          setDialogState(false);
        })
        .catch((error) => {
          if (error.status && error.status === 403) {
            seterrorInfo(t("AgentAssistantInfoDialog-ErrorInformation"));
            console.log(error);
          } else {
            console.log(error);
          }
        });
    } else if (operation === "update" || operation === "reactivate") {
      await updateAgentAssistant(
        assistantuserid,
        data.firstname,
        data.lastname,
        data.phoneNumber,
        data.email,
        data.postalCode,
        isChecked.viewCommissions === "Yes" ? 1 : 0
      )
        .then((data) => {
          setPageLoad(0);
          getuserPermissions();
          setDialogState(false);
        })
        .catch((error) => {
          if (error.status && error.status === 403) {
            seterrorInfo(t("AgentAssistantInfoDialog-ErrorInformation"));
            console.log(error);
          } else if (error.status && error.status === 404) {
            seterrorInfo(t("AgentAssistantInfoDialog-RecordNotFound"));
          } else {
            seterrorInfo(error);
            console.log(error);
          }
        });
    }
    setLoading(false);
    reset();
  }

  return (
    <Dialog
      open={dialogState}
      onClose={() => setDialogState(false)}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {title && title.length > 0
              ? title
              : t("AgentAssistantDetails-AddAssistant-Title")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label={t(
              "AgentAssistantInfoDialog-CloseContactInformationWindow"
            )}
            onClick={() => setDialogState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            {description && description.length > 0
              ? description
              : t("AgentAssistantInfoDialog-ProvideInformation")}
          </Dialog.Description>
          <hr />
          {errorInfo.length > 0 ? (
            <p className="text-sm ui-input-error text-left mb-6">{errorInfo}</p>
          ) : (
            <div></div>
          )}
          <form id="assitantform" onSubmit={handleSubmit(onSubmit)}>
            <EverlakeLoadingOverlay isLoading={loading}>
              <div className="w-full mx-auto grid grid-cols-1 gap-4 sm:w-4/5 md:w-3/4 lg:w-3/5">
                {/* First name */}
                <div>
                  <label id={`${uuid}-firstname-label`}>
                    {t("AgentAssistantInfoDialog-FirstNameRequired")}
                  </label>
                  <input
                    id={`${uuid}-firstname`}
                    disabled={isDisabled}
                    className="w-full"
                    type="text"
                    defaultValue={assistantfname}
                    {...register("firstname", {
                      required: t("AgentAssistantInfoDialog-FirstName"),
                      maxLength: {
                        value: 50,
                        message: "AgentAssistantInfoDialog-Less50FirstName",
                      },
                    })}
                    aria-describedby={`${uuid}-firstname-error`}
                    aria-labelledby={`${uuid}-firstname-label`}
                  />
                  {registrationErrors.firstname && (
                    <FormInputError
                      id={`${uuid}-firstname-error`}
                      errorMessage={registrationErrors.firstname.message}
                    />
                  )}
                </div>
                {/* Last name */}
                <div>
                  <label id={`${uuid}-lastname-label`}>
                    {t("AgentAssistantInfoDialog-LastNameRequired")}
                  </label>
                  <input
                    id={`${uuid}-lastname`}
                    className="w-full"
                    disabled={isDisabled}
                    type="text"
                    defaultValue={assistantlname}
                    {...register("lastname", {
                      required: t("AgentAssistantInfoDialog-LastName"),
                      maxLength: {
                        value: 50,
                        message: t("AgentAssistantInfoDialog-Less50LastName"),
                      },
                    })}
                    aria-describedby={`${uuid}-lastname-error`}
                    aria-labelledby={`${uuid}-lastname-label`}
                  />
                  {registrationErrors.lastname && (
                    <FormInputError
                      id={`${uuid}-lastname-error`}
                      errorMessage={registrationErrors.lastname.message}
                    />
                  )}
                </div>

                {/* Email */}
                <div>
                  <label id={`${uuid}-emailLabel`}>
                    {t("AgentAssistantInfoDialog-EmailAddress")}
                  </label>
                  <input
                    id={`${uuid}-email`}
                    className="w-full"
                    type="text"
                    disabled={isDisabled}
                    defaultValue={assistantemail}
                    {...register("email", {
                      required: t(
                        "AgentAssistantInfoDialog-AllstateEmailAddress"
                      ),
                      pattern: {
                        value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                        message: t("AgentAssistantInfoDialog-ValidEmail"),
                      },
                      validate: (value) => {
                        const domainList =
                          config.ALLOWED_EMAIL_DOMAINLIST.split(";");
                        return isAllowedEmail(value, domainList);
                      },
                    })}
                    aria-describedby={`${uuid}-emailError`}
                    aria-labelledby={`${uuid}-emailLabel`}
                  />

                  {registrationErrors.email && (
                    <FormInputError
                      id={`${uuid}-emailError`}
                      errorMessage={registrationErrors.email.message}
                    />
                  )}
                </div>
                {/* Mobile */}
                <div>
                  <label id={`${uuid}-phone-label`}>
                    {t("AgentAssistantInfoDialog-MobileNumber")}
                  </label>
                  <MaskedPhoneNumberInput
                    name="phoneNumber"
                    describedby={`${uuid}-phone-label`}
                    register={register}
                    setValue={setValue}
                    defaultValue={assistantmobile}
                    required={false}
                  />
                  {registrationErrors.phoneNumber && (
                    <FormInputError
                      id={`${uuid}-phone-error`}
                      errorMessage={registrationErrors.phoneNumber.message}
                    />
                  )}
                  <span id={`${uuid}-phone-format`} className="sr-only">
                    Enter as (XXX) XXX-XXXX
                  </span>
                </div>
                <div>
                  <label
                    id={`${UUID}-zip-label`}
                    htmlFor={`${UUID}-zip`}
                    className="block mb-1"
                  >
                    {t("AgentAssistantInfoDialog-BusinessZipCode")}
                  </label>
                  <input
                    id={`${UUID}-zip`}
                    aria-labelledby={`${UUID}-zip-label`}
                    aria-required="true"
                    type="text"
                    defaultValue={assistantzip}
                    disabled={isDisabled}
                    {...register("postalCode", {
                      required: t(
                        "AgentAssistantInfoDialog-AssistantBusinessZipCode"
                      ),
                      pattern: {
                        value: /^\d{5}$/,
                        message: t("AgentAssistantInfoDialog-ValidZipCode"),
                      },
                      maxLength: {
                        value: 5,
                        message: t("AgentAssistantInfoDialog-5DigitsZipCode"),
                      },
                    })}
                  />
                  {registrationErrors.postalCode && (
                    <FormInputError
                      id={`${uuid}-postalCode-error`}
                      errorMessage={registrationErrors.postalCode.message}
                    />
                  )}
                </div>
                {/* City
                <div>
                  <label
                    id={`${UUID}-city-label`}
                    htmlFor={`${UUID}-city`}
                    className="block mb-1"
                  >
                    City
                  </label>
                  <input
                    id={`${UUID}-city`}
                    aria-labelledby={`${UUID}-city-label`}
                    aria-required="true"
                    type="text"
                    disabled={false}
                    {...register("city")}
                  />
                </div>
                <div>
                  <label
                    id={`${UUID}-state-label`}
                    htmlFor={`${UUID}-state`}
                    className="block mb-1"
                  >
                    State
                  </label>
                  <select
                    id={`${UUID}-state`}
                    aria-labelledby={`${UUID}-state-label`}
                    {...register("state")}
                    disabled={false}
                  >
                    <option value="">Select</option>
                    {states.map((state, idx) => {
                      return (
                        <option
                          key={`state selection - ${idx}`}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              */}
                <fieldset>
                  <label id={`${uuid}-phone-label`}>
                    {t("AgentAssistantInfoDialog-DisplayCommissions")}
                  </label>
                  <br />
                  <div className="inline-block mt-1 mr-6">
                    <label
                      htmlFor="ID_viewCommissionsYes"
                      className="inline-flex items-center text-sm font-normal"
                    >
                      <input
                        type="radio"
                        name="viewCommissions"
                        value="Yes"
                        onChange={handleOnChange}
                        checked={isChecked.viewCommissions === "Yes"}
                        id="ID_viewCommissionsYes"
                        className="mr-2"
                        disabled={isDisabled}
                      />
                      {t("AgentAssistantInfoDialog-Yes")}
                    </label>
                  </div>
                  <div className="inline-block">
                    <label
                      htmlFor="ID_viewCommissionsNo"
                      className="inline-flex items-center text-sm font-normal"
                    >
                      <input
                        type="radio"
                        name="viewCommissions"
                        value="No"
                        onChange={handleOnChange}
                        checked={isChecked.viewCommissions === "No"}
                        id="ID_viewCommissionsNo"
                        className="mr-2"
                        disabled={isDisabled}
                      />
                      {t("AgentAssistantInfoDialog-No")}
                    </label>
                  </div>
                </fieldset>
              </div>
              {/* <!-- Buttons --> */}
              {operation === "view" ? (
                <div className="block text-right mt-6"></div>
              ) : (
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    {t("AgentAssistantInfoDialog-Cancel")}
                    <span className="sr-only">
                      {t(
                        "AgentAssistantInfoDialog-changesAssistantInformation"
                      )}
                    </span>
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    {t("AgentAssistantInfoDialog-Save")}
                    <span className="sr-only">
                      {t(
                        "AgentAssistantInfoDialog-changesAssistantInformation"
                      )}
                    </span>
                  </button>
                </div>
              )}
            </EverlakeLoadingOverlay>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
